/**
 * Schema helpers for the CrossTenantAccessSettings baseline.
 *
 * The baseline maps to the Microsoft Graph cross-tenant access policy default
 * (`GET /policies/crossTenantAccessPolicy/default`). The JSON schema constrains the
 * security-relevant fields with `const` values: the inbound trust toggles and, for each
 * B2B collaboration / direct-connect direction plus tenant restrictions, the
 * users-and-groups and applications access types.
 *
 * The applications and tenant-restriction fields are optional on the shared types so the
 * (unchanged) template component, which only edits the inbound-trust + users-and-groups
 * access types, keeps compiling and producing the same schema shape.
 */

import { CrossTenantAccessPolicyDefault } from 'projects/angular-clarity/src/app/stores/client/graph/cross-tenant-access-policy/actions';

export type AccessType = 'allowed' | 'blocked';

/**
 * Who an outbound users-and-groups partition applies to:
 * - `all` — every external user and group (`AllUsers` target).
 * - `select` — only the explicitly selected users and/or groups.
 */
export type AppliesTo = 'all' | 'select';

/** A setting that carries both a users-and-groups and an applications access type. */
export type SettingKey =
    | 'b2bCollaborationInbound'
    | 'b2bCollaborationOutbound'
    | 'b2bDirectConnectInbound'
    | 'b2bDirectConnectOutbound'
    | 'tenantRestrictions';

/**
 * Only the outbound directions allow narrowing the users-and-groups partition to specific
 * users/groups; inbound and tenant-restriction targets are always `AllUsers` in the default policy.
 */
export type OutboundSettingKey = 'b2bCollaborationOutbound' | 'b2bDirectConnectOutbound';

export type AccessPartition = 'usersAndGroups' | 'applications';

/** A single entry of a partition's `targets` array on the live policy / schema. */
export interface CrossTenantTarget {
    target: string;
    targetType: string;
}

/**
 * Schema for an outbound users-and-groups `targets` array. Rather than pinning the whole array with
 * a single `const` (which is order-sensitive), each required target is asserted with its own
 * `contains` clause inside `allOf`, and `minItems`/`maxItems` lock the exact count — the same shape
 * the Admin Consent Workflow baseline uses for its reviewers. This makes deviation detection
 * order-independent.
 */
interface OutboundTargetsSchema {
    type: 'array';
    minItems: number;
    maxItems: number;
    allOf?: {
        contains: {
            type: 'object';
            properties: {
                target: { type: 'string'; const: string };
                targetType: { type: 'string'; const: string };
            };
        };
    }[];
}

interface BooleanConst {
    type: 'boolean';
    const: boolean;
}

interface AccessTypePartitionConst {
    type: 'object';
    properties: {
        accessType: { type: 'string'; const: AccessType };
        /** Present only for outbound users-and-groups partitions that pin specific targets. */
        targets?: OutboundTargetsSchema;
    };
    required: ['accessType'];
}

interface AccessTypeConst {
    type: 'object';
    properties: {
        usersAndGroups: AccessTypePartitionConst;
        applications?: AccessTypePartitionConst;
    };
}

export interface CrossTenantAccessSettingsSchema {
    type: 'object';
    properties: {
        inboundTrust?: {
            type: 'object';
            properties: {
                isMfaAccepted?: BooleanConst;
                isCompliantDeviceAccepted?: BooleanConst;
                isHybridAzureADJoinedDeviceAccepted?: BooleanConst;
            };
        };
        b2bCollaborationInbound?: AccessTypeConst;
        b2bCollaborationOutbound?: AccessTypeConst;
        b2bDirectConnectInbound?: AccessTypeConst;
        b2bDirectConnectOutbound?: AccessTypeConst;
        tenantRestrictions?: AccessTypeConst;
    };
}

export interface CrossTenantAccessSettingsFormValue {
    isMfaAccepted: boolean;
    isCompliantDeviceAccepted: boolean;
    isHybridAzureADJoinedDeviceAccepted: boolean;
    // users-and-groups access types
    b2bCollaborationInbound: AccessType;
    b2bCollaborationOutbound: AccessType;
    b2bDirectConnectInbound: AccessType;
    b2bDirectConnectOutbound: AccessType;
    tenantRestrictions?: AccessType;
    // applications access types (optional — only the tenant baseline edits these)
    b2bCollaborationInboundApps?: AccessType;
    b2bCollaborationOutboundApps?: AccessType;
    b2bDirectConnectInboundApps?: AccessType;
    b2bDirectConnectOutboundApps?: AccessType;
    tenantRestrictionsApps?: AccessType;
    // outbound users-and-groups target selection (optional — only the tenant baseline edits these)
    b2bCollaborationOutboundAppliesTo?: AppliesTo;
    b2bCollaborationOutboundUsers?: string[];
    b2bCollaborationOutboundGroups?: string[];
    b2bDirectConnectOutboundAppliesTo?: AppliesTo;
    b2bDirectConnectOutboundUsers?: string[];
    b2bDirectConnectOutboundGroups?: string[];
}

/** Secure/default users-and-groups access type per setting. */
const USERS_DEFAULTS: Record<SettingKey, AccessType> = {
    b2bCollaborationInbound: 'allowed',
    b2bCollaborationOutbound: 'allowed',
    b2bDirectConnectInbound: 'blocked',
    b2bDirectConnectOutbound: 'blocked',
    tenantRestrictions: 'allowed',
};

/** Read an access type for a given setting + partition out of an existing schema. */
function readSchemaAccessType(
    schema: CrossTenantAccessSettingsSchema | undefined,
    key: SettingKey,
    partition: AccessPartition,
): AccessType | undefined {
    return schema?.properties?.[key]?.properties?.[partition]?.properties?.accessType?.const;
}

/**
 * Read the pinned users-and-groups `targets` for an outbound setting out of an existing schema,
 * reconstructing them from the `allOf`/`contains` clauses into plain target entries.
 */
function readSchemaTargets(
    schema: CrossTenantAccessSettingsSchema | undefined,
    key: OutboundSettingKey,
): CrossTenantTarget[] | undefined {
    const targetsSchema = schema?.properties?.[key]?.properties?.usersAndGroups?.properties?.targets;
    if (!targetsSchema) return undefined;
    return (targetsSchema.allOf ?? []).map((clause) => ({
        target: clause.contains?.properties?.target?.const,
        targetType: clause.contains?.properties?.targetType?.const,
    }));
}

/** Whether a `targets` array represents "all users" (the `AllUsers` sentinel target). */
function targetsAreAllUsers(targets: CrossTenantTarget[] | undefined): boolean {
    return !targets || targets.length === 0 || targets.some((t) => t.target === 'AllUsers');
}

/**
 * Build the Graph `targets` array for an outbound users-and-groups partition from the form
 * selection. `all` collapses to the `AllUsers` sentinel; `select` lists each user/group id.
 */
export function buildOutboundTargets(appliesTo: AppliesTo, users: string[], groups: string[]): CrossTenantTarget[] {
    if (appliesTo === 'select') {
        return [
            ...users.map((id) => ({ target: id, targetType: 'user' })),
            ...groups.map((id) => ({ target: id, targetType: 'group' })),
        ];
    }
    return [{ target: 'AllUsers', targetType: 'user' }];
}

/**
 * Build the `targets` JSON-schema for an outbound users-and-groups partition. Each required target
 * gets its own `contains` clause inside `allOf`, with `minItems`/`maxItems` enforcing the exact set.
 */
function buildOutboundTargetsSchema(appliesTo: AppliesTo, users: string[], groups: string[]): OutboundTargetsSchema {
    const targets = buildOutboundTargets(appliesTo, users, groups);
    if (targets.length === 0) {
        return { type: 'array', minItems: 0, maxItems: 0 };
    }
    return {
        type: 'array',
        minItems: targets.length,
        maxItems: targets.length,
        allOf: targets.map((t) => ({
            contains: {
                type: 'object',
                properties: {
                    target: { type: 'string', const: t.target },
                    targetType: { type: 'string', const: t.targetType },
                },
            },
        })),
    };
}

/** Read the desired form values out of an existing schema, applying secure defaults where absent. */
export function extractCrossTenantAccessSettings(
    schema: CrossTenantAccessSettingsSchema | undefined,
): CrossTenantAccessSettingsFormValue {
    const inboundTrust = schema?.properties?.inboundTrust?.properties;

    const users = (key: SettingKey): AccessType =>
        readSchemaAccessType(schema, key, 'usersAndGroups') ?? USERS_DEFAULTS[key];

    /**
     * Applications must always follow users-and-groups: apps cannot be more permissive
     * (blocked users → blocked apps) nor more restrictive (allowed users → allowed apps).
     * Always return the users-and-groups value so the two partitions stay in sync.
     */
    const apps = (key: SettingKey): AccessType => users(key);

    /**
     * Outbound target selection — "all users" when the schema pins no targets at all, or pins the
     * `AllUsers` sentinel. An existing targets schema without the sentinel means "select users and
     * groups", including the empty set: in select mode the ids may be left entirely to tenant-level
     * overrides, which pins `minItems`/`maxItems` of 0 with no target clauses.
     */
    const appliesTo = (key: OutboundSettingKey): AppliesTo => {
        const targets = readSchemaTargets(schema, key);
        if (!targets) return 'all';
        return targets.some((t) => t.target === 'AllUsers') ? 'all' : 'select';
    };
    const selectedTargets = (key: OutboundSettingKey, targetType: string): string[] =>
        readSchemaTargets(schema, key)
            ?.filter((t) => t.targetType === targetType && t.target !== 'AllUsers')
            .map((t) => t.target) ?? [];

    return {
        isMfaAccepted: inboundTrust?.isMfaAccepted?.const ?? true,
        isCompliantDeviceAccepted: inboundTrust?.isCompliantDeviceAccepted?.const ?? true,
        isHybridAzureADJoinedDeviceAccepted: inboundTrust?.isHybridAzureADJoinedDeviceAccepted?.const ?? true,
        b2bCollaborationInbound: users('b2bCollaborationInbound'),
        b2bCollaborationOutbound: users('b2bCollaborationOutbound'),
        b2bDirectConnectInbound: users('b2bDirectConnectInbound'),
        b2bDirectConnectOutbound: users('b2bDirectConnectOutbound'),
        tenantRestrictions: users('tenantRestrictions'),
        b2bCollaborationInboundApps: apps('b2bCollaborationInbound'),
        b2bCollaborationOutboundApps: apps('b2bCollaborationOutbound'),
        b2bDirectConnectInboundApps: apps('b2bDirectConnectInbound'),
        b2bDirectConnectOutboundApps: apps('b2bDirectConnectOutbound'),
        tenantRestrictionsApps: apps('tenantRestrictions'),
        b2bCollaborationOutboundAppliesTo: appliesTo('b2bCollaborationOutbound'),
        b2bCollaborationOutboundUsers: selectedTargets('b2bCollaborationOutbound', 'user'),
        b2bCollaborationOutboundGroups: selectedTargets('b2bCollaborationOutbound', 'group'),
        b2bDirectConnectOutboundAppliesTo: appliesTo('b2bDirectConnectOutbound'),
        b2bDirectConnectOutboundUsers: selectedTargets('b2bDirectConnectOutbound', 'user'),
        b2bDirectConnectOutboundGroups: selectedTargets('b2bDirectConnectOutbound', 'group'),
    };
}

function partitionConst(accessType: AccessType, targets?: OutboundTargetsSchema): AccessTypePartitionConst {
    const partition: AccessTypePartitionConst = {
        type: 'object',
        properties: { accessType: { type: 'string', const: accessType } },
        required: ['accessType'],
    };
    if (targets !== undefined) {
        partition.properties.targets = targets;
    }
    return partition;
}

function toAccessTypeConst(
    users: AccessType,
    apps?: AccessType,
    usersTargets?: OutboundTargetsSchema,
): AccessTypeConst {
    const value: AccessTypeConst = {
        type: 'object',
        properties: { usersAndGroups: partitionConst(users, usersTargets) },
    };
    if (apps !== undefined) {
        value.properties.applications = partitionConst(apps);
    }
    return value;
}

/** Build the outbound users-and-groups `targets` schema from a form value, or `undefined` when unset. */
function outboundTargets(
    appliesTo: AppliesTo | undefined,
    users: string[] | undefined,
    groups: string[] | undefined,
): OutboundTargetsSchema | undefined {
    if (appliesTo === undefined) return undefined;
    return buildOutboundTargetsSchema(appliesTo, users ?? [], groups ?? []);
}

/**
 * Resolve the applications access type for a setting, enforcing that applications always
 * follow users-and-groups. Applications cannot be more permissive (blocked users → blocked apps)
 * nor more restrictive (allowed users → allowed apps) than the users-and-groups partition.
 */
function resolveAppsAccessType(usersValue: AccessType): AccessType {
    return usersValue;
}

/** Build a CrossTenantAccessSettings schema from the desired-value form. */
export function generateCrossTenantAccessSettingsSchema(
    formValue: CrossTenantAccessSettingsFormValue,
): CrossTenantAccessSettingsSchema {
    const schema: CrossTenantAccessSettingsSchema = {
        type: 'object',
        properties: {
            inboundTrust: {
                type: 'object',
                properties: {
                    isMfaAccepted: { type: 'boolean', const: formValue.isMfaAccepted },
                    isCompliantDeviceAccepted: { type: 'boolean', const: formValue.isCompliantDeviceAccepted },
                    isHybridAzureADJoinedDeviceAccepted: {
                        type: 'boolean',
                        const: formValue.isHybridAzureADJoinedDeviceAccepted,
                    },
                },
            },
            b2bCollaborationInbound: toAccessTypeConst(
                formValue.b2bCollaborationInbound,
                resolveAppsAccessType(formValue.b2bCollaborationInbound),
            ),
            b2bCollaborationOutbound: toAccessTypeConst(
                formValue.b2bCollaborationOutbound,
                resolveAppsAccessType(formValue.b2bCollaborationOutbound),
                outboundTargets(
                    formValue.b2bCollaborationOutboundAppliesTo,
                    formValue.b2bCollaborationOutboundUsers,
                    formValue.b2bCollaborationOutboundGroups,
                ),
            ),
            b2bDirectConnectInbound: toAccessTypeConst(
                formValue.b2bDirectConnectInbound,
                resolveAppsAccessType(formValue.b2bDirectConnectInbound),
            ),
            b2bDirectConnectOutbound: toAccessTypeConst(
                formValue.b2bDirectConnectOutbound,
                resolveAppsAccessType(formValue.b2bDirectConnectOutbound),
                outboundTargets(
                    formValue.b2bDirectConnectOutboundAppliesTo,
                    formValue.b2bDirectConnectOutboundUsers,
                    formValue.b2bDirectConnectOutboundGroups,
                ),
            ),
        },
    };

    // Tenant restrictions are only edited by the tenant baseline — omit them entirely when absent
    // so the (unchanged) template component keeps producing the same schema shape.
    if (formValue.tenantRestrictions !== undefined) {
        schema.properties.tenantRestrictions = toAccessTypeConst(
            formValue.tenantRestrictions,
            resolveAppsAccessType(formValue.tenantRestrictions),
        );
    }

    return schema;
}

/** Read the current access type from the live policy object for a setting + partition. */
export function readPolicyAccessType(
    data: CrossTenantAccessPolicyDefault | undefined,
    key: SettingKey,
    partition: AccessPartition = 'usersAndGroups',
): AccessType | undefined {
    return data?.[key]?.[partition]?.accessType;
}

/** Read the current users-and-groups `targets` from the live policy object for an outbound setting. */
export function readPolicyTargets(
    data: CrossTenantAccessPolicyDefault | undefined,
    key: OutboundSettingKey,
): CrossTenantTarget[] | undefined {
    return data?.[key]?.usersAndGroups?.targets as CrossTenantTarget[] | undefined;
}

/** The "applies to" mode currently configured on the live policy for an outbound setting. */
export function policyAppliesTo(data: CrossTenantAccessPolicyDefault | undefined, key: OutboundSettingKey): AppliesTo {
    return targetsAreAllUsers(readPolicyTargets(data, key)) ? 'all' : 'select';
}
